// @catalog F.006
// PeriodSelector — chips MTD/1M/3M/YTD/12M/24M + mapper a DateRange ISO.
// Reemplaza el DatePicker dual de FilterBar como control primario.
import { Box } from "@mui/material";

export type PeriodKey = "mtd" | "1m" | "3m" | "ytd" | "12m" | "24m";

// El valor mostrado puede ser un período preset o "custom" cuando el usuario
// aplicó un rango manual desde los DatePickers (F1). En "custom" ningún chip
// preset queda resaltado y se muestra el indicador "Personalizado".
export type PeriodValue = PeriodKey | "custom";

interface PeriodSelectorProps {
  value: PeriodValue;
  onChange: (next: PeriodKey) => void;
}

const OPTIONS: Array<{ key: PeriodKey; label: string }> = [
  { key: "mtd", label: "MTD" },
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "ytd", label: "YTD" },
  { key: "12m", label: "12M" },
  { key: "24m", label: "24M" },
];

export interface DateRangeISO {
  start: string | null;
  end: string | null;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Convierte un PeriodKey en un DateRange ISO concreto.
// Reglas:
//   - mtd: primer día del mes actual..hoy
//   - 1m: hace 30 días..hoy
//   - 3m: hace 90 días..hoy
//   - ytd: 1 enero del año actual..hoy
//   - 12m: hace 365 días..hoy
//   - 24m: hace 730 días..hoy
export function periodToDateRange(
  period: PeriodKey,
  reference: Date = new Date(),
): DateRangeISO {
  const today = new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
  );
  const end = isoFromDate(today);

  if (period === "mtd") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: isoFromDate(start), end };
  }
  if (period === "ytd") {
    const start = new Date(today.getFullYear(), 0, 1);
    return { start: isoFromDate(start), end };
  }
  const daysByPeriod: Record<Exclude<PeriodKey, "mtd" | "ytd">, number> = {
    "1m": 30,
    "3m": 90,
    "12m": 365,
    "24m": 730,
  };
  const offset = daysByPeriod[period];
  const startMs = today.getTime() - offset * 24 * 60 * 60 * 1000;
  const start = new Date(startMs);
  return { start: isoFromDate(start), end };
}

export function PeriodSelector(props: PeriodSelectorProps): JSX.Element {
  const { value, onChange } = props;
  return (
    <Box
      role="group"
      aria-label="Selector de periodo"
      sx={{
        display: "inline-flex",
        padding: "3px",
        backgroundColor: "var(--bg-2)",
        borderRadius: "8px",
        border: "1px solid var(--border)",
      }}
    >
      {OPTIONS.map((opt) => {
        const isActive = opt.key === value;
        return (
          <Box
            key={opt.key}
            component="button"
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(opt.key)}
            sx={{
              padding: "5px 11px",
              fontSize: "12.5px",
              color: isActive ? "var(--ink-1)" : "var(--ink-2)",
              borderRadius: "5px",
              fontWeight: 500,
              backgroundColor: isActive ? "var(--surface)" : "transparent",
              boxShadow: isActive
                ? "0 1px 2px rgba(13, 18, 32, 0.08), 0 0 0 1px var(--border)"
                : "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "background-color 0.15s ease, color 0.15s ease",
              "&:hover": isActive
                ? {}
                : { color: "var(--ink-1)", backgroundColor: "var(--bg)" },
              "&:focus-visible": {
                outline: "2px solid var(--accent)",
                outlineOffset: "1px",
              },
            }}
          >
            {opt.label}
          </Box>
        );
      })}
      {value === "custom" && (
        <Box
          component="span"
          aria-current="true"
          sx={{
            padding: "5px 11px",
            fontSize: "12.5px",
            color: "var(--ink-1)",
            borderRadius: "5px",
            fontWeight: 500,
            backgroundColor: "var(--surface)",
            boxShadow:
              "0 1px 2px rgba(13, 18, 32, 0.08), 0 0 0 1px var(--border)",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          Personalizado
        </Box>
      )}
    </Box>
  );
}
